import CheckIcon from 'assets/images/check.svg';
import FormInput from 'components/FormInput';
import { useFormik } from 'formik';
import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetProjectChecklistsMutation } from 'redux/services/checklists';
import { onSubmitParams } from './types';

interface ChecklistType {
  _id: string;
  libraryId: string;
  name: string;
  description: string;
  isProjectLibrary: boolean;
}

interface ChecklistsProps {
  isSingleEdit?: boolean;
  disabled?: boolean;
  onSubmitProject: (params: onSubmitParams) => void;
  projectDetails: string;
}

const ProjectChecklist = ({
  isSingleEdit,
  disabled,
  onSubmitProject,
  projectDetails,
}: ChecklistsProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { step, setupId: projectId } = useParams();
  const [getChecklists, { data, isSuccess, isError }] =
    useGetProjectChecklistsMutation();
  const [checklists, setChecklists] = useState([]);
  const [searchText, setSearchText] = useState('');
  const initialValues = {
    checklistLibraryId: projectDetails,
  };

  const formik = useFormik({
    initialValues: initialValues,
    onSubmit: (values) => {
      const params = {
        values: {
          checklistLibraryId: values.checklistLibraryId,
        },
        draft: true,
        redirectAfterSubmit: true,
      };
      onSubmitProject(params);
    },
  });

  const getAllChecklists = useCallback(async () => {
    const res: any = await getChecklists(projectId);
    if (res.data) {
      setChecklists(res.data);
    } else {
      toast.error(res.error.data.message || t('something_went_wrong'));
    }
  }, [projectId, getChecklists, t]);

  const checklistFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
    const filterData = data.filter((list: ChecklistType) => {
      const regEx = new RegExp(e.target.value, 'i');
      return list.name.match(regEx);
    });
    setChecklists(filterData);
  };

  useEffect(() => {
    if (!checklists.length && !isSuccess && !isError) {
      getAllChecklists();
    }
  }, [checklists, isSuccess, isError, getAllChecklists]);

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    formik.setFieldValue('checklistLibraryId', event.target.value);

  return (
    <div className="checklist  h-100 overflow-auto d-flex flex-column">
      <form
        className="h-100 overflow-auto d-flex flex-column"
        onSubmit={formik.handleSubmit}
      >
        <div className="step-wrapper flex-grow-1 overflow-auto">
          <FormInput
            type="search"
            placeholder={t('input_search_placeholder')}
            name="search"
            value={searchText}
            onChange={checklistFilter}
          />
          {checklists.map((item: ChecklistType) => (
            <Card
              className={
                formik.values.checklistLibraryId === item.libraryId ||
                item.isProjectLibrary
                  ? 'active'
                  : ''
              }
              key={item.libraryId}
            >
              <label
                id="residential"
                className="d-flex align-items-center justify-content-between mb-0"
              >
                <input
                  type="radio"
                  name="checklist"
                  id={item.libraryId}
                  value={item.libraryId}
                  onChange={handleRadioChange}
                  hidden
                />
                <p className="mb-0">{item.name}</p>
                <span>
                  {formik.values.checklistLibraryId === item.libraryId && (
                    <img src={CheckIcon} width="16" alt="" />
                  )}
                </span>
              </label>
            </Card>
          ))}
        </div>
        {isSingleEdit ? (
          <div className="d-flex justify-space-between">
            <Button
              variant="outline-primary"
              onClick={() => {
                navigate(-1);
              }}
            >
              {t('btn_back')}
            </Button>
            <Button
              hidden={disabled}
              type="submit"
              className="ms-auto"
              variant="primary"
            >
              {t('btn_save-exit')}
            </Button>
          </div>
        ) : (
          <div className="d-flex justify-space-between">
            <Button
              variant="outline-primary"
              onClick={() => {
                navigate({
                  pathname: `/project/setup/${projectId}/${Number(step) - 1}`,
                });
              }}
            >
              {t('btn_back')}
            </Button>
            <Button type="submit" className="ms-auto" variant="primary">
              {t('btn_save-next')}
            </Button>
          </div>
        )}
      </form>
    </div>
  );
};

export default ProjectChecklist;
